import { designOpenPlan, type OpenPlan } from "@shared/design-edit-scope.ts";
import type { DesignTemplate } from "@shared/design-template.ts";
import { finalPdfPollMs } from "@shared/final-pdf.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArtworkLibrary, type LibraryRow } from "../components/ArtworkLibrary.tsx";
import {
  Card,
  EmptyState,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  PageHeader,
  Pagination,
  ROW_SCROLL,
  usePageSize,
} from "../components/account-ui.tsx";
import { DesignEditPrompt } from "../components/DesignEditPrompt.tsx";
import { LoadingSpinnerWithBackground, useToast } from "../components/legacy/ui.tsx";
import { NewArtworkMenu } from "../components/NewArtworkMenu.tsx";
import { CampaignStatusBadge, FinalPdfBadge } from "../components/status.tsx";
import { CheckCircleIcon } from "../icons/index.tsx";
import { type Artwork, api, type Campaign, type Product } from "../lib/api.ts";
import {
  type ArtworkListSearch,
  buildLibraryRows,
  campaignsUsingArtwork,
  libraryRowThumbSrc,
  parseArtworkListSearch,
} from "../lib/artwork-list.ts";
import { fetchDesignUsage, postEditScope } from "../lib/design-edit.ts";
import {
  artworkNeedsApproval,
  effectiveStatus,
  formatDate,
  isStaffRole,
  type PageSize,
} from "../lib/legacy.ts";
import { useProfile } from "./account.tsx";

export const Route = createFileRoute("/account/artwork")({
  component: ArtworkPage,
  validateSearch: parseArtworkListSearch,
});

// Dynamic: the builder client pulls in the editor bundle (Fabric).
const loadBuilder = () => import("../lib/artwork-builder.ts");

function ArtworkPage() {
  const search = Route.useSearch();
  const { approval } = search;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const profile = useProfile().data?.profile;
  const userId = profile?.id ?? "";
  const staff = isStaffRole(profile?.role);
  const designs = useQuery({
    queryKey: ["designs", userId],
    queryFn: async () => (await loadBuilder()).listDesigns(userId),
    enabled: Boolean(userId),
  });
  const templates = useQuery({
    queryKey: ["design-templates", userId],
    queryFn: () => api.get<{ templates: DesignTemplate[] }>("/designs/templates"),
    enabled: Boolean(userId) && !approval,
  });
  const openEditor = (designId: string, from?: "templates", ready = false) =>
    navigate({
      to: "/account/design/$designId",
      params: { designId },
      search: {
        ...(from === "templates" ? { from: "templates" as const } : {}),
        ...(ready ? { scope: "ready" as const } : {}),
      },
    });
  const openDesign = async (designId: string, from?: "templates") => {
    try {
      const rows = await fetchDesignUsage(designId);
      const plan = designOpenPlan(rows, { designId });
      if (plan.kind === "prompt") {
        setEditPrompt({ designId, plan, from });
        return;
      }
      if (plan.kind === "editor") {
        await openEditor(designId, from);
        return;
      }
      const hit = rows.find((row) => row.id === plan.campaignId);
      if (!hit?.customerId) return;
      await navigate({
        to: "/app/$customerId/mycampaigns/$campaignId",
        params: { customerId: hit.customerId, campaignId: hit.id },
        search: { panel: "artwork" },
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not open that design", "error");
    }
  };
  const confirmEditScope = async (mode: "all" | "campaign") => {
    if (!editPrompt) return;
    setPending(true);
    try {
      if (mode === "all") {
        await postEditScope(editPrompt.designId, { mode: "all" });
        const from = editPrompt.from;
        const designId = editPrompt.designId;
        setEditPrompt(null);
        await openEditor(designId, from, true);
        return;
      }
      const target = editPrompt.plan.justThis;
      const hit = editPrompt.plan.changeable.find((row) => row.id === target);
      if (!target || !hit?.customerId) return;
      const copyId = await postEditScope(editPrompt.designId, {
        mode: "campaign",
        campaignId: target,
      });
      setEditPrompt(null);
      await navigate({
        to: "/app/$customerId/design/$campaignId/$designId",
        params: { customerId: hit.customerId, campaignId: hit.id, designId: copyId },
        search: { scope: "ready" },
      });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not change that design", "error");
    } finally {
      setPending(false);
    }
  };
  const startFromTemplate = async (templateId: string) => {
    const { design } = await api.post<{ design: { id: string } }>(
      `/designs/from-template/${templateId}`,
    );
    void queryClient.invalidateQueries({ queryKey: ["designs", userId] });
    openDesign(design.id);
  };
  const design = useMutation({
    mutationFn: async (size: PageSize) =>
      (
        await (await loadBuilder()).builderClient(userId).createArtwork({
          name: `Untitled design ${size.label}`,
          target: "print300",
          page: { width: size.width, height: size.height, bleed: 3 },
          pageCount: size.pages,
        })
      ).id,
    onSuccess: (id) => {
      // Browser Back skips the editor's own refresh, so mark the list stale now.
      void queryClient.invalidateQueries({ queryKey: ["designs", userId] });
      openDesign(id);
    },
    onError: (e: Error) => showToast(e.message || "Could not start a design", "error"),
  });
  const artworks = useQuery({
    queryKey: ["artwork"],
    queryFn: () => api.get<{ artwork: Artwork[] }>("/artwork"),
  });
  const campaigns = useQuery({
    queryKey: ["campaigns", "all"],
    queryFn: () => api.get<{ campaigns: Campaign[] }>("/campaigns?includeDeleted=1"),
    enabled: Boolean(approval),
    refetchInterval: (query) => finalPdfPollMs(query.state.data?.campaigns),
  });
  const usageCampaigns = useQuery({
    queryKey: ["campaigns", "usage"],
    queryFn: () => api.get<{ campaigns: Campaign[] }>("/campaigns?includeDeleted=1"),
    enabled: Boolean(userId) && !approval,
  });
  const catalog = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.get<{ products: Product[] }>("/catalog/products"),
  });
  const [pending, setPending] = useState(false);
  const [editPrompt, setEditPrompt] = useState<{
    designId: string;
    plan: Extract<OpenPlan, { kind: "prompt" }>;
    from?: "templates";
  } | null>(null);
  const [perPage, setPerPage] = usePageSize();
  const approvalKey = String(perPage);
  const [approvalPaged, setApprovalPaged] = useState({ key: approvalKey, n: 0 });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      form.append("name", file.name);
      return api.upload<{ artwork: Artwork }>("/artwork", form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["artwork"] });
      showToast("Artwork uploaded", "success");
    },
    onError: (e: Error) => showToast(e.message || "Upload failed", "error"),
  });

  const convert = useMutation({
    mutationFn: async (input: File | { id: string; name: string }) => {
      const { artworkPdfFile, importPdfDesign } = await import("../lib/pdf-import.ts");
      const file = input instanceof File ? input : await artworkPdfFile(input.id, input.name);
      const client = (await loadBuilder()).builderClient(userId);
      return importPdfDesign(client, file);
    },
    onSuccess: async (imported) => {
      const { importSummary } = await import("../lib/pdf-import.ts");
      await queryClient.invalidateQueries({ queryKey: ["designs", userId] });
      showToast(importSummary(imported.report), "success");
      openDesign(imported.id);
    },
    onError: (e: Error) => showToast(e.message || "Could not convert that PDF", "error"),
  });

  const refreshLibrary = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["artwork"] }),
      queryClient.invalidateQueries({ queryKey: ["designs", userId] }),
      queryClient.invalidateQueries({ queryKey: ["design-templates", userId] }),
    ]);

  const markTemplate = async (row: LibraryRow, scope: "personal" | "global") => {
    setPending(true);
    try {
      await api.post(`/designs/${row.id}/template`, { scope });
      await queryClient.invalidateQueries({ queryKey: ["design-templates", userId] });
      showToast(scope === "global" ? "Template is now global" : "Saved as a template", "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not update that template", "error");
    } finally {
      setPending(false);
    }
  };

  const unmarkTemplate = async (row: LibraryRow) => {
    setPending(true);
    try {
      await api.delete(`/designs/${row.id}/template`);
      await queryClient.invalidateQueries({ queryKey: ["design-templates", userId] });
      showToast("Removed from templates", "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not update that template", "error");
    } finally {
      setPending(false);
    }
  };

  const useTemplate = async (row: LibraryRow) => {
    setPending(true);
    try {
      await startFromTemplate(row.id);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not start from that template", "error");
    } finally {
      setPending(false);
    }
  };

  const rename = async (row: LibraryRow, name: string) => {
    setPending(true);
    try {
      if (row.draft) await (await loadBuilder()).builderClient(userId).renameArtwork(row.id, name);
      else await api.patch(`/artwork/${row.id}`, { name });
      await refreshLibrary();
      showToast("Artwork renamed successfully", "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to rename artwork", "error");
      throw e;
    } finally {
      setPending(false);
    }
  };

  const remove = async (row: LibraryRow) => {
    setPending(true);
    try {
      if (row.draft) {
        await (await loadBuilder()).builderClient(userId).deleteArtwork(row.id);
        if (row.proofId) await api.delete(`/artwork/${row.proofId}`);
      } else {
        await api.delete(`/artwork/${row.id}`);
      }
      await refreshLibrary();
      showToast("Artwork deleted", "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not delete artwork", "error");
    } finally {
      setPending(false);
    }
  };

  const duplicate = async (row: LibraryRow) => {
    setPending(true);
    try {
      if (row.draft) await (await loadBuilder()).duplicateDesign(userId, row.id);
      else await api.post(`/artwork/${row.id}/duplicate`);
      await refreshLibrary();
      showToast("Artwork duplicated", "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not duplicate artwork", "error");
    } finally {
      setPending(false);
    }
  };

  const usageOf = async (row: LibraryRow) => {
    const listed = artworks.data?.artwork ?? [];
    const { campaigns: all } = await queryClient.fetchQuery({
      queryKey: ["campaigns", "usage"],
      queryFn: () => api.get<{ campaigns: Campaign[] }>("/campaigns?includeDeleted=1"),
    });
    return campaignsUsingArtwork(row, listed, all).map((campaign) => ({
      id: campaign.id,
      name: campaign.name || "Untitled Campaign",
      status: effectiveStatus(campaign),
      customerId: campaign.customer_id ?? campaign.user_id ?? userId,
    }));
  };

  // Staff get every customer's artwork from GET /artwork; this page is their own library.
  const owned = (artworks.data?.artwork ?? []).filter((a) => !a.user_id || a.user_id === userId);
  const products = catalog.data?.products ?? [];
  const templateById = new Map((templates.data?.templates ?? []).map((t) => [t.id, t]));
  const designById = new Map((designs.data ?? []).map((d) => [d.id, d]));
  const built = buildLibraryRows(
    owned,
    (designs.data ?? []).map((d) => ({
      id: d.id,
      name: d.name,
      createdAt: d.createdAt,
      updatedAt: d.updatedAt,
      width: d.width,
      height: d.height,
      pageCount: d.pageCount,
    })),
  );
  const libraryRows: LibraryRow[] = built.map((row) => {
    const design = row.draft ? designById.get(row.id) : undefined;
    const next = {
      ...row,
      mailPanelPage: design?.mailPanelPage ?? null,
      ...(row.draft
        ? {
            templateScope: templateById.get(row.id)?.scope ?? null,
            ownerId: userId,
          }
        : {}),
    };
    return { ...next, thumbSrc: libraryRowThumbSrc(next, design?.listThumb) };
  });
  const templateRows: LibraryRow[] = (templates.data?.templates ?? []).map((t) => {
    const design = designById.get(t.id);
    const row = {
      id: t.id,
      name: t.name,
      created_at: t.createdAt,
      updated_at: t.updatedAt,
      design_id: t.id,
      width: t.width,
      height: t.height,
      pages: t.pageCount,
      draft: true as const,
      locked: built.find((item) => item.id === t.id)?.locked ?? false,
      proofId: null,
      templateScope: t.scope,
      ownerId: t.ownerId,
      mailPanelPage: design?.mailPanelPage ?? null,
    };
    return { ...row, thumbSrc: libraryRowThumbSrc(row, design?.listThumb) };
  });
  const apply = (patch: Partial<ArtworkListSearch>, replace = false) => {
    const next: ArtworkListSearch = { ...search, ...patch };
    for (const key of Object.keys(next) as (keyof ArtworkListSearch)[]) {
      if (next[key] === undefined) delete next[key];
    }
    navigate({ to: "/account/artwork", search: next, replace });
  };
  const reset = () =>
    navigate({
      to: "/account/artwork",
      search: {
        ...(search.view === "templates" ? { view: "templates" as const } : {}),
        ...(approval ? { approval: true } : {}),
      },
    });
  const awaiting = (campaigns.data?.campaigns ?? [])
    .filter((c) => (c.customer_id ?? c.user_id) === userId && artworkNeedsApproval(c))
    .sort((a, b) => b.updated_at - a.updated_at);
  const approvalPage = Math.min(
    approvalPaged.key === approvalKey ? approvalPaged.n : 0,
    Math.max(0, Math.ceil(awaiting.length / perPage) - 1),
  );
  const awaitingPage = awaiting.slice(approvalPage * perPage, (approvalPage + 1) * perPage);

  return (
    <div className={FILL_PAGE}>
      {(upload.isPending || convert.isPending || pending || design.isPending) && (
        <LoadingSpinnerWithBackground />
      )}
      {editPrompt && (
        <DesignEditPrompt
          plan={editPrompt.plan}
          pending={pending}
          onCancel={() => setEditPrompt(null)}
          onAll={
            editPrompt.plan.changeable.length > 0 ? () => void confirmEditScope("all") : undefined
          }
          onJustThis={
            editPrompt.plan.justThis ? () => void confirmEditScope("campaign") : undefined
          }
        />
      )}
      <PageHeader
        title={approval ? "Artwork approval" : "Artwork Files"}
        subtitle={
          approval
            ? "Open a campaign to review and approve its artwork."
            : "Your artwork library. Pick a file for a campaign from inside that campaign."
        }
        action={
          approval ? undefined : (
            <NewArtworkMenu
              disabled={!userId}
              onCreate={(size) => design.mutate(size)}
              onUpload={(file, opts) => (opts.convert ? convert.mutate(file) : upload.mutate(file))}
              onFromTemplate={async (templateId) => {
                try {
                  await startFromTemplate(templateId);
                } catch (e) {
                  showToast(
                    e instanceof Error ? e.message : "Could not start from that template",
                    "error",
                  );
                  throw e;
                }
              }}
            />
          )
        }
      />

      {approval && (
        <Card title="Awaiting approval" className={FILL_CARD} bodyClass={FILL_BODY}>
          {awaiting.length === 0 ? (
            <EmptyState
              Icon={CheckCircleIcon}
              title={campaigns.isPending ? "Loading campaigns" : "Nothing awaiting approval"}
              message="Campaigns with artwork you have not approved yet show up here."
            />
          ) : (
            <>
              <ul className={`${ROW_SCROLL} divide-y divide-gray-100`}>
                {awaitingPage.map((c) => (
                  <li key={c.id}>
                    <Link
                      to="/app/$customerId/mycampaigns/$campaignId"
                      params={{
                        customerId: c.customer_id ?? c.user_id ?? userId,
                        campaignId: c.id,
                      }}
                      search={{ panel: "artwork" }}
                      className="flex items-center gap-3 px-5 py-4 text-sm transition-colors hover:bg-gray-50"
                    >
                      <CampaignStatusBadge status={effectiveStatus(c)} showLabel={false} />
                      <FinalPdfBadge
                        status={c.final_pdf_status}
                        updatedAt={c.final_pdf_updated_at}
                      />
                      <span className="min-w-0 flex-1 truncate font-medium text-gray-900">
                        {c.name || "Untitled Campaign"}
                      </span>
                      <span className="shrink-0 text-xs text-gray-500">
                        {formatDate(c.updated_at)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Pagination
                page={approvalPage}
                pageSize={perPage}
                total={awaiting.length}
                onPage={(n) => setApprovalPaged({ key: approvalKey, n })}
                onPageSize={setPerPage}
              />
            </>
          )}
        </Card>
      )}

      {!approval && (
        <ArtworkLibrary
          mode="library"
          rows={search.view === "templates" ? templateRows : libraryRows}
          products={products}
          search={search}
          onSearchChange={apply}
          onReset={reset}
          onRename={rename}
          onDelete={remove}
          onDuplicate={duplicate}
          usageFiles={artworks.data?.artwork ?? []}
          usageCampaigns={usageCampaigns.data?.campaigns ?? null}
          onUsage={usageOf}
          onEditDesign={openDesign}
          onConvertPdf={(row) => convert.mutate({ id: row.id, name: row.name })}
          staff={staff}
          userId={userId}
          onUseTemplate={useTemplate}
          onSaveAsTemplate={(row, scope) => markTemplate(row, scope)}
          onMakeGlobal={(row) => markTemplate(row, "global")}
          onUnmarkTemplate={unmarkTemplate}
        />
      )}
    </div>
  );
}
